import { Star, Quote } from "lucide-react";
import { useSite } from "../context/SiteContentContext";

export const Testimonials = () => {
    const siteConfig = useSite();
    const testimonials = siteConfig.testimonials;

    if (!testimonials || !testimonials.items || testimonials.items.length === 0)
        return null;

    return (
        <section
            id="testimonials"
            data-testid="testimonials-section"
            className="relative py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] border-t border-[#262626] overflow-hidden"
        >
            {/* Decorative quote */}
            <div
                aria-hidden
                className="absolute top-12 right-12 md:top-20 md:right-32 text-[#1a1a1c] pointer-events-none select-none"
            >
                <Quote
                    size={280}
                    strokeWidth={0.5}
                    className="rotate-180 md:size-[420px]"
                />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-20">
                    <div className="lg:col-span-6">
                        <div className="ornamental-divider mb-6">
                            <span>Testemunhos</span>
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#f5f5f0]">
                            {testimonials.sectionTitle}
                            <br />
                            <span className="italic font-normal text-[#a3a3a3]">
                                {testimonials.sectionSubtitle}
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 flex items-end">
                        <div className="flex items-baseline gap-4">
                            <span className="font-display text-6xl md:text-7xl font-black text-[#d4af37] tabular-nums leading-none">
                                4.8
                            </span>
                            <div>
                                <div className="flex gap-0.5 text-[#d4af37] mb-1">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <Star
                                            key={i}
                                            size={16}
                                            fill="currentColor"
                                        />
                                    ))}
                                </div>
                                <div className="text-xs tracking-widest uppercase text-[#737373]">
                                    52+ reviews · Google
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {testimonials.items.map((t, idx) => (
                        <TestimonialCard
                            key={idx}
                            testimonial={t}
                            index={idx}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

const TestimonialCard = ({ testimonial, index }) => {
    const { name, quote, rating = 5, source } = testimonial;

    return (
        <article
            data-testid={`testimonial-card-${index}`}
            className="relative group bg-[#121212] border border-[#262626] p-7 md:p-8 hover:border-[#d4af37]/40 transition-colors duration-500"
        >
            {/* Stars */}
            <div className="flex gap-0.5 text-[#d4af37] mb-5">
                {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                        key={i}
                        size={14}
                        fill={i < rating ? "currentColor" : "none"}
                        strokeWidth={i < rating ? 0 : 1.5}
                        className={
                            i < rating ? "text-[#d4af37]" : "text-[#3a3a3a]"
                        }
                    />
                ))}
            </div>

            {/* Quote */}
            <blockquote className="font-body text-[#f5f5f0] text-base md:text-lg leading-relaxed mb-8">
                <span className="text-[#d4af37] font-display text-3xl leading-none mr-1 align-top">
                    “
                </span>
                {quote}
                <span className="text-[#d4af37] font-display text-3xl leading-none ml-1 align-bottom">
                    ”
                </span>
            </blockquote>

            {/* Footer */}
            <footer className="flex items-center justify-between pt-5 border-t border-[#1f1f1f]">
                <div>
                    <div className="font-display text-base font-semibold text-[#f5f5f0]">
                        {name}
                    </div>
                    {source && (
                        <div className="text-xs tracking-widest uppercase text-[#737373] mt-0.5">
                            via {source}
                        </div>
                    )}
                </div>
            </footer>
        </article>
    );
};
